// Current UI language ('am' | 'en'), persisted to localStorage so a reload
// doesn't silently reset a sales user's choice. Root CLAUDE.md: "Test every
// screen in both languages before considering it done" — this is the one
// switch every screen reads to know which language to render, nothing more.

import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { DEFAULT_LANGUAGE, LANGUAGES, t as translate } from '../i18n.js';

const STORAGE_KEY = 'sneethel.admin.lang';

const LanguageContext = createContext(null);

function readStoredLanguage() {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return LANGUAGES.includes(stored) ? stored : DEFAULT_LANGUAGE;
  } catch {
    // localStorage can throw in a locked-down browser context (private
    // mode quota, etc.) — falling back to the default is always safe.
    return DEFAULT_LANGUAGE;
  }
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(readStoredLanguage);

  const setLang = useCallback((next) => {
    if (!LANGUAGES.includes(next)) return;
    setLangState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Non-fatal — the choice just won't survive a reload this time.
    }
    document.documentElement.lang = next;
  }, []);

  const toggle = useCallback(() => {
    setLang(lang === 'am' ? 'en' : 'am');
  }, [lang, setLang]);

  const t = useCallback((id, vars) => translate(id, lang, vars), [lang]);

  const value = useMemo(() => ({ lang, setLang, toggle, t }), [lang, setLang, toggle, t]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useLanguage() must be used inside <LanguageProvider>.');
  }
  return ctx;
}
